/**
 * Email Reply Panel Component
 *
 * Reply composer panel.
 * This appears in the CENTER when Reply is clicked (Step 4).
 *
 * Layout:
 * - Header: Back button + "Reply" title
 * - Fields: From, To, Subject
 * - Tabs: Plain Text | Email Editor | Templates
 * - Footer: Attach, Save Draft, Send
 */

import React, { useState, useRef, useEffect } from "react";
import { FaArrowLeft, FaPaperclip, FaTrash } from "react-icons/fa";
import {
  EmailMessage,
  EmailConversation,
  EmailComposeData,
  EmailReplyTab,
  EmailTemplate,
} from "./types";
import { PlainTextTab } from "./plain-text-tab";
import { TemplatesTab } from "./templates-tab";
import { EmailEditorDialog } from "../../../components/email-editor-dialog";
import useRequest from "../../../../../hook/use-req";

const getCurrentUserEmail = (): string => {
  try {
    const raw =
      localStorage.getItem("userDetails") ||
      localStorage.getItem("user") ||
      localStorage.getItem("user_data");
    const user = raw ? JSON.parse(raw) : null;
    return user?.email || user?.org_email || "support@convoai.com";
  } catch {
    return "support@convoai.com";
  }
};

/**
 * Template Variables Modal
 */
interface TemplateVariablesModalProps {
  isOpen: boolean;
  template: EmailTemplate | null;
  variables: Record<string, string>;
  onVariableChange: (key: string, value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const TemplateVariablesModal: React.FC<TemplateVariablesModalProps> = ({
  isOpen,
  template,
  variables,
  onVariableChange,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen || !template) return null;

  const allFieldsFilled = template.variables.every(
    (v) => variables[v]?.trim() !== "",
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[1001] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-gray-800">
          <h3 className="text-base font-semibold text-slate-800 dark:text-gray-100">
            Fill Template Variables
          </h3>
          <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
            Enter values for all variables in "{template.name}"
          </p>
        </div>

        {/* Variables Form */}
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            {template.variables.map((variable) => (
              <div key={variable} className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium text-gray-700 dark:text-gray-300 capitalize">
                  {variable.replace(/_/g, " ")}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  value={variables[variable] || ""}
                  onChange={(e) => onVariableChange(variable, e.target.value)}
                  placeholder={`Enter ${variable.replace(/_/g, " ")}`}
                  className="px-3 py-2.5 text-[13px] text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 bg-white dark:bg-gray-800"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-800/60">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!allFieldsFilled}
            className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Use Template
          </button>
        </div>
      </div>
    </div>
  );
};

interface EmailReplyPanelProps {
  conversation: EmailConversation;
  selectedEmail: EmailMessage;
  onSend: (data: EmailComposeData) => void;
  onSaveDraft: (data: EmailComposeData) => void;
  onBack: () => void;
}

/**
 * Tab configuration
 */
const tabs: { id: EmailReplyTab; label: string }[] = [
  { id: "plain-text", label: "Plain Text" },
  { id: "email-editor", label: "Email Editor" },
  { id: "templates", label: "Templates" },
];

/**
 * Email Reply Panel
 */
export const EmailReplyPanel: React.FC<EmailReplyPanelProps> = ({
  conversation,
  selectedEmail,
  onSend,
  onSaveDraft,
  onBack,
}) => {
  // Form state
  const [activeTab, setActiveTab] = useState<EmailReplyTab>("plain-text");
  const [toEmails, setToEmails] = useState<string>(selectedEmail.from.email);
  const [subject, setSubject] = useState<string>(selectedEmail.subject);
  const [plainTextContent, setPlainTextContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<
    Record<string, string>
  >({});
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isEditorDialogOpen, setIsEditorDialogOpen] = useState(false);
  const [isVariablesModalOpen, setIsVariablesModalOpen] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<EmailTemplate | null>(
    null,
  );
  const [editorHtmlContent, setEditorHtmlContent] = useState("");
  const [editorDesign, setEditorDesign] = useState<object | null>(null);
  const currentUserEmail = getCurrentUserEmail();

  // Templates state
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // API hook for fetching templates
  const { makeRequest: fetchTemplates } = useRequest(
    "/api/email/templates",
    "GET",
  );

  // Fetch templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const [response] = await fetchTemplates();
        if (response?.success) {
          setTemplates(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch email templates:", error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle file attachment
   */
  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Handle template selection - opens modal if template has variables
   */
  const handleSelectTemplate = (template: EmailTemplate) => {
    if (template.variables && template.variables.length > 0) {
      // Template has variables - show modal first
      setPendingTemplate(template);

      // Check if this is the same template that's already selected (editing)
      const isEditingExisting = selectedTemplate?.id === template.id;

      if (!isEditingExisting) {
        // New template selection - initialize empty variables
        const initialVariables: Record<string, string> = {};
        template.variables.forEach((v) => {
          initialVariables[v] = "";
        });
        setTemplateVariables(initialVariables);
      }
      // If editing existing, keep the current templateVariables

      setIsVariablesModalOpen(true);
    } else {
      // No variables - select template directly
      setSelectedTemplate(template);
      setEditorHtmlContent(template.html_body);
      setSubject(template.subject);
      setTemplateVariables({});
    }
  };

  /**
   * Handle confirming template selection after filling variables
   */
  const handleConfirmTemplateSelection = () => {
    if (pendingTemplate) {
      setSelectedTemplate(pendingTemplate);
      setEditorHtmlContent(pendingTemplate.html_body);
      setSubject(pendingTemplate.subject);
      setPendingTemplate(null);
      setIsVariablesModalOpen(false);
    }
  };

  /**
   * Handle canceling template variable modal
   */
  const handleCancelTemplateSelection = () => {
    // Only clear variables if we were selecting a new template (not editing existing)
    const wasEditingExisting = selectedTemplate?.id === pendingTemplate?.id;
    setPendingTemplate(null);
    if (!wasEditingExisting) {
      setTemplateVariables({});
    }
    setIsVariablesModalOpen(false);
  };

  /**
   * Handle editing variables of already selected template
   */
  const handleEditVariables = (template?: EmailTemplate) => {
    const targetTemplate = template || selectedTemplate;
    if (targetTemplate) {
      setPendingTemplate(targetTemplate);
      setIsVariablesModalOpen(true);
    }
  };

  /**
   * Handle template variable change
   */
  const handleVariableChange = (variableName: string, value: string) => {
    setTemplateVariables((prev) => ({
      ...prev,
      [variableName]: value,
    }));
  };

  /**
   * Get processed HTML with variables replaced
   */
  const getProcessedHtml = (html: string): string => {
    let processedHtml = html;
    Object.entries(templateVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      processedHtml = processedHtml.replace(regex, value || `{{${key}}}`);
    });
    return processedHtml;
  };

  /**
   * Handle tab click - open dialog for email editor
   */
  const handleTabClick = (tabId: EmailReplyTab) => {
    if (tabId === "email-editor") {
      setIsEditorDialogOpen(true);
    } else {
      setActiveTab(tabId);
    }
  };

  /**
   * Handle save from email editor dialog
   */
  const handleEditorSave = (html: string, design: object) => {
    setEditorHtmlContent(html);
    setEditorDesign(design);
    setActiveTab("email-editor");
  };

  /**
   * Get email content based on active tab
   */
  const getEmailContent = async (): Promise<{
    html: string;
    plainText: string;
  }> => {
    if (activeTab === "plain-text") {
      const html = `<p>${plainTextContent.replace(/\n/g, "</p><p>")}</p>`;
      return { html, plainText: plainTextContent };
    } else if (activeTab === "email-editor" || editorHtmlContent) {
      const rawHtml = editorHtmlContent || selectedTemplate?.html_body || "";
      // Process template variables
      const html = getProcessedHtml(rawHtml);
      const plainText =
        selectedTemplate?.text_body || html.replace(/<[^>]*>/g, "");
      return { html, plainText };
    } else if (activeTab === "templates" && selectedTemplate) {
      // Use selected template with variables replaced
      const html = getProcessedHtml(selectedTemplate.html_body);
      const plainText = getProcessedHtml(
        selectedTemplate.text_body || html.replace(/<[^>]*>/g, ""),
      );
      return { html, plainText };
    }
    return { html: "", plainText: "" };
  };

  /**
   * Handle send email
   */
  const handleSend = async () => {
    setIsSending(true);
    try {
      const { html, plainText } = await getEmailContent();
      const data: EmailComposeData = {
        to: toEmails
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
        subject,
        body: html,
        plainText,
        attachments,
      };
      onSend(data);
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Handle save draft
   */
  const handleSaveDraft = async () => {
    try {
      const { html, plainText } = await getEmailContent();
      const data: EmailComposeData = {
        to: toEmails
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
        subject,
        body: html,
        plainText,
        attachments,
        isDraft: true,
      };
      onSaveDraft(data);
    } catch (error) {
      console.error("Failed to save draft:", error);
    }
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex-1 bg-white dark:bg-gray-900 flex flex-col h-full overflow-hidden">
      {/* Header with Back button */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-800/60">
        <button
          onClick={onBack}
          className="p-1.5 text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 hover:bg-slate-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <FaArrowLeft size={14} />
        </button>
        <h2 className="text-base font-semibold text-slate-800 dark:text-gray-100">Reply</h2>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Reply Section */}
        <div className="bg-white dark:bg-gray-900">
          {/* Email Fields */}
          <div className="px-5 py-3 space-y-2">
            {/* From Field */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 w-14">From:</span>
              <div className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
                <span className="text-[11px] text-gray-600 dark:text-gray-300">
                  {currentUserEmail}
                </span>
              </div>
            </div>

            {/* To Field */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 w-14">To:</span>
              <input
                type="text"
                value={toEmails}
                onChange={(e) => setToEmails(e.target.value)}
                placeholder="recipient@example.com"
                className="flex-1 px-3 py-2 text-[11px] text-gray-600 dark:text-gray-200 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md outline-none focus:border-[#7C83FD]"
              />
            </div>

            {/* Subject Field */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 w-14">Subject:</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="flex-1 px-3 py-2 text-[11px] text-gray-700 dark:text-gray-200 dark:bg-gray-800 font-medium border border-gray-200 dark:border-gray-700 rounded-md outline-none focus:border-[#7C83FD]"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 px-5 py-3 bg-white dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`px-4 py-2 text-[12px] font-medium rounded-md transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-[#7C83FD] text-white shadow-md"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#7C83FD] dark:hover:border-indigo-400 hover:text-[#7C83FD] dark:hover:text-indigo-400"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[200px] bg-white dark:bg-gray-900">
            {activeTab === "plain-text" && (
              <PlainTextTab
                value={plainTextContent}
                onChange={setPlainTextContent}
              />
            )}
            {activeTab === "email-editor" && (
              <div className="px-5 py-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">
                    Email Content:
                  </label>
                  {editorHtmlContent && (
                    <button
                      onClick={() => setIsEditorDialogOpen(true)}
                      className="px-3 py-1 text-[11px] font-medium text-[#7C83FD] dark:text-indigo-400 border border-[#7C83FD] dark:border-indigo-400 rounded-md hover:bg-[#7C83FD] hover:text-white transition-colors"
                    >
                      Edit Template
                    </button>
                  )}
                </div>
                {editorHtmlContent ? (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <div
                      className="text-[13px] text-gray-700 dark:text-gray-200 dark:prose-invert leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: editorHtmlContent }}
                    />
                  </div>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-8 bg-gray-50 dark:bg-gray-800 text-center">
                    <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-3">
                      No email template created yet
                    </p>
                    <button
                      onClick={() => setIsEditorDialogOpen(true)}
                      className="px-4 py-2 text-[12px] font-medium text-white bg-[#7C83FD] rounded-md hover:bg-[#6b73e8] transition-colors"
                    >
                      Open Email Editor
                    </button>
                  </div>
                )}
              </div>
            )}
            {activeTab === "templates" && (
              <TemplatesTab
                templates={templates}
                onSelectTemplate={handleSelectTemplate}
                onEditVariables={handleEditVariables}
                selectedTemplateId={selectedTemplate?.id}
                loading={templatesLoading}
                templateVariables={templateVariables}
              />
            )}
          </div>
        </div>
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-gray-800/60">
          <p className="text-xs font-medium text-slate-500 dark:text-gray-400 mb-2">
            Attachments ({attachments.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-800 rounded border border-slate-200 dark:border-gray-700"
              >
                <FaPaperclip size={10} className="text-slate-400 dark:text-gray-500" />
                <span className="text-xs text-slate-600 dark:text-gray-300">{file.name}</span>
                <span className="text-[10px] text-slate-400 dark:text-gray-500">
                  ({formatFileSize(file.size)})
                </span>
                <button
                  onClick={() => removeAttachment(index)}
                  className="p-0.5 text-slate-400 dark:text-gray-500 hover:text-red-500 transition-colors"
                >
                  <FaTrash size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={handleAttachFile}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-gray-300 bg-slate-100 dark:bg-gray-800 rounded-lg hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors"
          >
            <FaPaperclip size={10} />
            Attach
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveDraft}
            className="px-4 py-2 text-xs font-medium text-slate-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
          >
            Save as Draft
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || !toEmails || !subject}
            className="px-5 py-2 text-xs font-medium text-white bg-indigo-500 rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>

      {/* Email Editor Dialog */}
      <EmailEditorDialog
        isOpen={isEditorDialogOpen}
        onClose={() => setIsEditorDialogOpen(false)}
        onSave={handleEditorSave}
        initialDesign={editorDesign || undefined}
      />

      {/* Template Variables Modal */}
      <TemplateVariablesModal
        isOpen={isVariablesModalOpen}
        template={pendingTemplate}
        variables={templateVariables}
        onVariableChange={handleVariableChange}
        onConfirm={handleConfirmTemplateSelection}
        onCancel={handleCancelTemplateSelection}
      />
    </div>
  );
};
